import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import DurationInput from 'modules/core/components/DurationInput'
import i18n from 'modules/core/lib/i18n'

type Props = {
  timeOffMinutes: number
  payrollMinutes: number
  availableMinutes: number
  timeOffMaxMinutes?: number
  payrollEnabled: boolean
  timeOffEnabled: boolean
  onTimeOffChange: (minutes: number) => void
  onPayrollChange: (minutes: number) => void
  errors?: {
    timeOff?: string
    payroll?: string
    exceedsBalance?: string
  }
  exceedsBalance?: boolean
}

const AllocationInputs = ({
  timeOffMinutes,
  payrollMinutes,
  availableMinutes,
  timeOffMaxMinutes,
  payrollEnabled,
  timeOffEnabled,
  onTimeOffChange,
  onPayrollChange,
  errors,
  exceedsBalance: exceedsBalanceProp,
}: Props) => {
  const handleTimeOffChange = (minutes: number) => {
    // Respect Time Off max limit if it exists (overtime counter limit)
    // But allow values that exceed available balance - validation will show error
    const clampedMinutes = timeOffMaxMinutes
      ? Math.max(0, Math.min(minutes, timeOffMaxMinutes))
      : Math.max(0, minutes)

    onTimeOffChange(clampedMinutes)
  }

  const handlePayrollChange = (minutes: number) => {
    // Allow user to enter any value, validation will show error if total exceeds available
    const clampedMinutes = Math.max(0, minutes)
    onPayrollChange(clampedMinutes)
  }

  // Calculate if total exceeds available balance
  const totalAllocated = timeOffMinutes + payrollMinutes
  const exceedsBalance = exceedsBalanceProp ?? totalAllocated > availableMinutes

  // Determine error message to show (only for exceeds balance - show once below inputs)
  const exceedsBalanceErrorMessage =
    exceedsBalance || errors?.exceedsBalance
      ? errors?.exceedsBalance ||
        i18n.t(
          'clock_in.sidepanel.compensation_overview.flexible_compensation.error.exceeds_balance'
        )
      : undefined

  // Only show error message once below inputs, not on individual DurationInput components
  // Individual field errors (timeOff, payroll) can still show on their respective inputs
  const showErrorBelow = exceedsBalanceErrorMessage && !errors?.timeOff && !errors?.payroll

  return (
    <Box flexDirection="column" gap="s8">
      <Box flexDirection="row" gap="s16" alignItems="flexStart">
        {timeOffEnabled && (
          <Box flexGrow minWidth="s140" style={{ whiteSpace: 'nowrap' }}>
            <DurationInput
              label={i18n.t(
                'clock_in.sidepanel.compensation_overview.flexible_compensation.to_time_off'
              )}
              minutes={timeOffMinutes}
              onChange={handleTimeOffChange}
              error={errors?.timeOff ? errors.timeOff : undefined}
            />
          </Box>
        )}

        {payrollEnabled && (
          <Box flexGrow minWidth="s140" style={{ whiteSpace: 'nowrap' }}>
            <DurationInput
              label={i18n.t(
                'clock_in.sidepanel.compensation_overview.flexible_compensation.to_payroll'
              )}
              minutes={payrollMinutes}
              onChange={handlePayrollChange}
              error={errors?.payroll ? errors.payroll : undefined}
            />
          </Box>
        )}
      </Box>
      {showErrorBelow && (
        <Text.Normal size="300" color="moodSuperNegative">
          {exceedsBalanceErrorMessage}
        </Text.Normal>
      )}
    </Box>
  )
}

export default AllocationInputs
